import { type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import type {
  BuildLog,
  BuildLogEntry,
} from "skedyul-core/features/workflows/resources/types";
import { BuildLogSchema } from "skedyul-core/features/workflows/resources/types";

// Infer types from tRPC router
export type DeploymentSummary =
  inferRouterOutputs<AppRouter>["apps"]["getDeployments"][number];

export type DeploymentDetail =
  inferRouterOutputs<AppRouter>["apps"]["getDeployment"];

export type DeploymentWorkflowsResponse =
  inferRouterOutputs<AppRouter>["apps"]["getDeploymentWorkflows"];

export type DeploymentWorkflowVersion =
  DeploymentWorkflowsResponse["workflowVersions"][number];

// Realtime update payload for deployment build logs
type DeploymentBuildUpdate = {
  deploymentId: string;
  logs?: BuildLogEntry[];
  status?: string;
};

// Migration approval required event payload
export type MigrationApprovalRequired = {
  deploymentId: string;
  migrationId: string;
  impacts: Array<{
    operationType: string;
    resourceType: string;
    resourceHandle: string;
    affectedRecords?: number;
    message?: string;
    isDestructive?: boolean;
  }>;
  timeoutMinutes: number;
  createdAt: string;
};

export interface DeploymentsState {
  // List of deployments for the deployments page
  deployments: {
    isLoading: boolean;
    isBulkCancelling: boolean;
    data: DeploymentSummary[];
    error: string | null;
  };
  // Single deployment detail (for detail page and sidebar)
  deployment: {
    isLoading: boolean;
    isRedeploying: boolean;
    isCancelling: boolean;
    data: DeploymentDetail | null;
    logs: BuildLog | null;
    pendingMigration: MigrationApprovalRequired | null;
    error: string | null;
  };
  // Deployment workflows (workflow versions bundled with a deployment)
  deploymentWorkflows: {
    isLoading: boolean;
    deploymentId: string | null;
    data: DeploymentWorkflowVersion[];
    error: string | null;
  };
}

const initialState: DeploymentsState = {
  deployments: {
    isLoading: false,
    isBulkCancelling: false,
    data: [],
    error: null,
  },
  deployment: {
    isLoading: false,
    isRedeploying: false,
    isCancelling: false,
    data: null,
    logs: null,
    pendingMigration: null,
    error: null,
  },
  deploymentWorkflows: {
    isLoading: false,
    deploymentId: null,
    data: [],
    error: null,
  },
};

// Async thunks
export const getDeployments = createAsyncThunk(
  "deployments/getDeployments",
  async (payload?: { limit?: number }) => {
    return await trpc.apps.getDeployments.query(payload);
  },
);

export const getDeployment = createAsyncThunk(
  "deployments/getDeployment",
  async (payload: { deploymentId: string; silent?: boolean }) => {
    return await trpc.apps.getDeployment.query({
      deploymentId: payload.deploymentId,
    });
  },
);

export const redeployDeployment = createAsyncThunk(
  "deployments/redeployDeployment",
  async (payload: { deploymentId: string; appId?: string }) => {
    return await trpc.apps.redeployDeployment.mutate(payload);
  },
);

export const getDeploymentWorkflows = createAsyncThunk(
  "deployments/getDeploymentWorkflows",
  async (payload: { deploymentId: string }) => {
    return await trpc.apps.getDeploymentWorkflows.query(payload);
  },
);

export const cancelDeployment = createAsyncThunk(
  "deployments/cancelDeployment",
  async (payload: { deploymentId: string }) => {
    return await trpc.apps.cancelDeployment.mutate(payload);
  },
);

export const cancelDeployments = createAsyncThunk(
  "deployments/cancelDeployments",
  async (payload: { deploymentIds: string[] }) => {
    return await trpc.apps.cancelDeployments.mutate(payload);
  },
);

export const deploymentsSlice = createSlice({
  name: "deployments",
  initialState,
  reducers: {
    // Clear the current deployment (useful when navigating away)
    clearDeployment: (state) => {
      state.deployment.data = null;
      state.deployment.logs = null;
      state.deployment.pendingMigration = null;
      state.deployment.error = null;
      state.deployment.isLoading = false;
    },
    // Clear deployment workflows (useful when navigating away)
    clearDeploymentWorkflows: (state) => {
      state.deploymentWorkflows.data = [];
      state.deploymentWorkflows.deploymentId = null;
      state.deploymentWorkflows.error = null;
      state.deploymentWorkflows.isLoading = false;
    },
    // Set deployment data directly (useful for initial load from provider)
    setDeployment: (
      state,
      action: PayloadAction<{
        deployment: DeploymentDetail | null;
        logs?: BuildLog | null;
      }>,
    ) => {
      state.deployment.data = action.payload.deployment;
      if (action.payload.logs !== undefined) {
        state.deployment.logs = action.payload.logs;
      } else if (action.payload.deployment?.logs) {
        // Parse logs from deployment if not provided separately
        try {
          state.deployment.logs = BuildLogSchema.parse(
            action.payload.deployment.logs,
          );
        } catch {
          state.deployment.logs = null;
        }
      }
      state.deployment.isLoading = false;
      state.deployment.error = null;
    },
    // Apply realtime build log updates from Pusher
    applyDeploymentBuildUpdate: (
      state,
      action: PayloadAction<DeploymentBuildUpdate>,
    ) => {
      const { deploymentId, logs: newLogs, status } = action.payload;

      // Only apply if this is for the current deployment
      if (state.deployment.data?.id !== deploymentId) return;

      // Update status if provided
      if (status && state.deployment.data) {
        state.deployment.data = {
          ...state.deployment.data,
          status: status as DeploymentDetail["status"],
        };
      }

      // Merge new logs with existing logs
      if (newLogs && Array.isArray(newLogs) && newLogs.length > 0) {
        const existingLogs = state.deployment.logs ?? [];

        // Validate new logs
        let validatedNewLogs: BuildLogEntry[] = [];
        try {
          validatedNewLogs = BuildLogSchema.parse(newLogs);
        } catch {
          // If parsing fails, ignore new logs
          return;
        }

        // Merge logs, avoiding duplicates based on timestamp and message
        const logMap = new Map<string, BuildLogEntry>();
        const allLogs: BuildLogEntry[] = [...existingLogs, ...validatedNewLogs];
        allLogs.forEach((log: BuildLogEntry) => {
          const key = `${log.timestamp}-${log.message}`;
          if (!logMap.has(key)) {
            logMap.set(key, log);
          }
        });

        state.deployment.logs = Array.from(logMap.values());
      }
    },
    // Update deployment status (e.g., when build completes)
    updateDeploymentStatus: (
      state,
      action: PayloadAction<{
        deploymentId: string;
        status: DeploymentDetail["status"];
      }>,
    ) => {
      const { deploymentId, status } = action.payload;

      // Update in deployment detail
      if (state.deployment.data?.id === deploymentId) {
        state.deployment.data = {
          ...state.deployment.data,
          status,
        };
      }

      // Update in deployments list
      const listIndex = state.deployments.data.findIndex(
        (d) => d.id === deploymentId,
      );
      if (listIndex !== -1) {
        const existing = state.deployments.data[listIndex];
        if (existing) {
          existing.status = status;
        }
      }
    },
    // Set pending migration approval from Pusher event
    setPendingMigration: (
      state,
      action: PayloadAction<MigrationApprovalRequired | null>,
    ) => {
      if (action.payload === null) {
        state.deployment.pendingMigration = null;
        return;
      }
      const { deploymentId, migrationId } = action.payload;
      if (state.deployment.data?.id === deploymentId) {
        state.deployment.pendingMigration = action.payload;
      }
      const listIndex = state.deployments.data.findIndex(
        (d) => d.id === deploymentId,
      );
      if (listIndex !== -1) {
        const existing = state.deployments.data[listIndex];
        if (existing) {
          state.deployments.data[listIndex] = {
            ...existing,
            migrationApproval: {
              status: "AWAITING_APPROVAL",
              migrationId,
            },
          };
        }
      }
    },
    // Clear pending migration (after approval/denial)
    clearPendingMigration: (state) => {
      const deploymentId =
        state.deployment.pendingMigration?.deploymentId ??
        state.deployment.data?.id;
      state.deployment.pendingMigration = null;
      if (!deploymentId) return;
      const listIndex = state.deployments.data.findIndex(
        (d) => d.id === deploymentId,
      );
      if (listIndex !== -1) {
        const existing = state.deployments.data[listIndex];
        if (existing) {
          state.deployments.data[listIndex] = {
            ...existing,
            migrationApproval: null,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getDeployments
      .addCase(getDeployments.pending, (state) => {
        state.deployments.isLoading = true;
        state.deployments.error = null;
      })
      .addCase(getDeployments.fulfilled, (state, action) => {
        state.deployments.isLoading = false;
        state.deployments.data = action.payload;
      })
      .addCase(getDeployments.rejected, (state, action) => {
        state.deployments.isLoading = false;
        state.deployments.error =
          action.error.message ?? "Failed to load deployments";
      })
      // getDeployment
      .addCase(getDeployment.pending, (state, action) => {
        if (!action.meta.arg.silent) {
          state.deployment.isLoading = true;
        }
        state.deployment.error = null;
      })
      .addCase(getDeployment.fulfilled, (state, action) => {
        if (action.payload.id !== action.meta.arg.deploymentId) {
          state.deployment.isLoading = false;
          return;
        }
        state.deployment.isLoading = false;
        state.deployment.data = action.payload;
        // Parse logs from deployment API response
        if (action.payload.logs) {
          try {
            const fetchedLogs = BuildLogSchema.parse(action.payload.logs);
            const isSilentInProgress =
              action.meta.arg.silent &&
              (action.payload.status === "QUEUED" ||
                action.payload.status === "BUILDING");
            const existingLogs = state.deployment.logs ?? [];

            if (isSilentInProgress && existingLogs.length > 0) {
              const logMap = new Map<string, BuildLogEntry>();
              [...existingLogs, ...fetchedLogs].forEach((log) => {
                const key = `${log.timestamp}-${log.message}`;
                if (!logMap.has(key)) {
                  logMap.set(key, log);
                }
              });
              state.deployment.logs = Array.from(logMap.values());
            } else {
              state.deployment.logs = fetchedLogs;
            }
          } catch {
            if (!state.deployment.logs?.length) {
              state.deployment.logs = null;
            }
          }
        } else if (
          state.deployment.data?.id === action.payload.id &&
          state.deployment.logs &&
          state.deployment.logs.length > 0
        ) {
          // Keep realtime logs when a refetch returns empty (e.g. CloudWatch miss)
        } else {
          state.deployment.logs = null;
        }
        // Set pending migration if present in API response
        if (action.payload.pendingMigration) {
          state.deployment.pendingMigration = {
            deploymentId: action.payload.id,
            migrationId: action.payload.pendingMigration.migrationId,
            impacts: action.payload.pendingMigration.impacts,
            timeoutMinutes: action.payload.pendingMigration.timeoutMinutes,
            createdAt: action.payload.pendingMigration.createdAt,
          };
        } else {
          state.deployment.pendingMigration = null;
        }

        const listIndex = state.deployments.data.findIndex(
          (d) => d.id === action.payload.id,
        );
        if (listIndex !== -1) {
          const existing = state.deployments.data[listIndex];
          if (existing) {
            state.deployments.data[listIndex] = {
              ...existing,
              status: action.payload.status,
              migrationApproval: action.payload.pendingMigration
                ? {
                    status: "AWAITING_APPROVAL",
                    migrationId: action.payload.pendingMigration.migrationId,
                  }
                : null,
            };
          }
        }
      })
      .addCase(getDeployment.rejected, (state, action) => {
        if (!action.meta.arg.silent) {
          state.deployment.isLoading = false;
          state.deployment.error =
            action.error.message ?? "Failed to load deployment";
        }
      })
      // redeployDeployment
      .addCase(redeployDeployment.pending, (state) => {
        state.deployment.isRedeploying = true;
      })
      .addCase(redeployDeployment.fulfilled, (state) => {
        state.deployment.isRedeploying = false;
      })
      .addCase(redeployDeployment.rejected, (state) => {
        state.deployment.isRedeploying = false;
      })
      // getDeploymentWorkflows
      .addCase(getDeploymentWorkflows.pending, (state, action) => {
        state.deploymentWorkflows.isLoading = true;
        state.deploymentWorkflows.error = null;
        state.deploymentWorkflows.deploymentId = action.meta.arg.deploymentId;
      })
      .addCase(getDeploymentWorkflows.fulfilled, (state, action) => {
        state.deploymentWorkflows.isLoading = false;
        state.deploymentWorkflows.data = action.payload.workflowVersions;
        state.deploymentWorkflows.deploymentId = action.payload.deploymentId;
      })
      .addCase(getDeploymentWorkflows.rejected, (state, action) => {
        state.deploymentWorkflows.isLoading = false;
        state.deploymentWorkflows.error =
          action.error.message ?? "Failed to load deployment workflows";
      })
      // cancelDeployment
      .addCase(cancelDeployment.pending, (state) => {
        state.deployment.isCancelling = true;
      })
      .addCase(cancelDeployment.fulfilled, (state, action) => {
        state.deployment.isCancelling = false;
        // Update the deployment status to FAILED
        if (state.deployment.data) {
          state.deployment.data = {
            ...state.deployment.data,
            status: "FAILED",
          };
        }
        // Also update in the list
        const deploymentId = action.meta.arg.deploymentId;
        const listIndex = state.deployments.data.findIndex(
          (d) => d.id === deploymentId,
        );
        if (listIndex !== -1) {
          const existing = state.deployments.data[listIndex];
          if (existing) {
            existing.status = "FAILED";
          }
        }
      })
      .addCase(cancelDeployment.rejected, (state) => {
        state.deployment.isCancelling = false;
      })
      // cancelDeployments (bulk)
      .addCase(cancelDeployments.pending, (state) => {
        state.deployments.isBulkCancelling = true;
      })
      .addCase(cancelDeployments.fulfilled, (state, action) => {
        state.deployments.isBulkCancelling = false;
        // Update the status of cancelled deployments in the list
        const cancelledIds = action.meta.arg.deploymentIds;
        for (const id of cancelledIds) {
          const listIndex = state.deployments.data.findIndex(
            (d) => d.id === id,
          );
          if (listIndex !== -1) {
            const existing = state.deployments.data[listIndex];
            if (
              existing &&
              (existing.status === "QUEUED" || existing.status === "BUILDING")
            ) {
              existing.status = "FAILED";
            }
          }
        }
      })
      .addCase(cancelDeployments.rejected, (state) => {
        state.deployments.isBulkCancelling = false;
      });
  },
});

// Export actions
export const {
  clearDeployment,
  clearDeploymentWorkflows,
  setDeployment,
  applyDeploymentBuildUpdate,
  updateDeploymentStatus,
  setPendingMigration,
  clearPendingMigration,
} = deploymentsSlice.actions;

export default deploymentsSlice.reducer;
